"use client";

import { c as _c } from "react/compiler-runtime";
import { Search } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCommandPalette } from "@/components/command-palette";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

/**
 * Visible, obviously-not-AI entry point for the global ⌘K palette (AIR-989):
 * a search-input-styled button ("bar") for the sidebar chrome, a compact
 * icon button ("icon") for narrow headers, or a small ⌘K chip ("rail") for
 * the collapsed icon rail's footer — bottom-left, where the pre-AIR-989
 * affordance lived (admin/profile routes collapse by default, and the rail's
 * magnifier alone proved undiscoverable). Renders nothing where the palette
 * provider is absent (logged-out shells).
 */
export function GlobalSearchTrigger(t0) {
  const $ = _c(37);
  const {
    variant: t1,
    className
  } = t0;
  const variant = t1 === undefined ? "bar" : t1;
  const t = useTranslations("globalSearch");
  const palette = useCommandPalette();
  if (!palette) {
    return null;
  }
  if (variant === "rail") {
    const t2 = palette.openPalette;
    let t3;
    if ($[0] !== t) {
      t3 = t("label");
      $[0] = t;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    let t4;
    if ($[2] !== className) {
      t4 = cn("justify-center", className);
      $[2] = className;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== t) {
      t5 = t("label");
      $[4] = t;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span className="text-muted-foreground ml-1 text-xs">⌘K</span>;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== t5) {
      t7 = {
        children: <span className="flex items-center gap-2">{t5} {t6}</span>
      };
      $[7] = t5;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Kbd className="border-sidebar-border bg-sidebar-accent text-sidebar-accent-foreground pointer-events-none border">⌘K</Kbd>;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    let t9;
    if ($[10] !== palette.openPalette || $[11] !== t3 || $[12] !== t4 || $[13] !== t7) {
      t9 = <SidebarMenuButton onClick={t2} aria-label={t3} data-testid="rail-cmdk-chip" className={t4} tooltip={t7}>{t8}</SidebarMenuButton>;
      $[10] = palette.openPalette;
      $[11] = t3;
      $[12] = t4;
      $[13] = t7;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    return t9;
  }
  if (variant === "icon") {
    let t2;
    if ($[15] !== t) {
      t2 = t("label");
      $[15] = t;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    let t3;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Search className="h-5 w-5" />;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    let t4;
    if ($[18] !== className || $[19] !== palette.openPalette || $[20] !== t2) {
      t4 = <Button type="button" variant="ghost" size="icon" className={className} aria-label={t2} onClick={palette.openPalette}>{t3}</Button>;
      $[18] = className;
      $[19] = palette.openPalette;
      $[20] = t2;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    return t4;
  }
  const t2 = palette.openPalette;
  let t3;
  if ($[22] !== t) {
    t3 = t("label");
    $[22] = t;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== className) {
    t4 = cn("flex h-8 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm text-muted-foreground shadow-none transition-colors outline-none", "hover:text-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50", className);
    $[24] = className;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Search className="size-4 shrink-0" />;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== t) {
    t6 = t("placeholder");
    $[27] = t;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t6) {
    t7 = <span className="flex-1 truncate text-left">{t6}</span>;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Kbd>⌘K</Kbd>;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== palette.openPalette || $[33] !== t3 || $[34] !== t4 || $[35] !== t7) {
    t9 = <button type="button" onClick={t2} aria-label={t3} className={t4}>{t5}{t7}{t8}</button>;
    $[32] = palette.openPalette;
    $[33] = t3;
    $[34] = t4;
    $[35] = t7;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
}